import React, { useEffect, useState, useRef } from "react";
import { format } from "d3-format";
import { curveMonotoneX } from "d3-shape";
import { v4 as uuidv4 } from "uuid";

// import { last } from 'react-financial-charts/lib/utils';
import {
  Annotate,
  BarSeries,
  CandlestickSeries,
  Chart,
  ChartCanvas,
  CrossHairCursor,
  discontinuousTimeScaleProviderBuilder,
  Label,
  LabelAnnotation,
  lastVisibleItemBasedZoomAnchor,
  LineSeries,
  MouseCoordinateY,
  MovingAverageTooltip,
  OHLCSeries,
  OHLCTooltip,
  SARSeries,
  XAxis,
  YAxis,
} from "react-financial-charts";
import PPOIndicator from "./indicators/PPOIndicator";
import PVOIndicator from "./indicators/PVOIndicator";
import MACDHIndicator from "./indicators/MACDHIndicator";
import MACDIndicator from "./indicators/MACDIndicator";
import CCIIndicator from "./indicators/CCIIndicator";
import CMFIndicator from "./indicators/CMFIndicator";
import { nFormatter } from "../../utils/utils";
import AnnotationStage from "./AnnotationStage";
import RSIIndicator from "./indicators/RSIIndicator";
import MFIIndicator from "./indicators/MFIIndicator";
import EFIndicator from "./indicators/EFIIndicator";
import moment from "moment";
import { useLocation } from "react-router-dom"; // Assuming you're using React Router for routing

const ChartComp = ({
  initialData = [],
  setting,
  setSetting,
  gridSetting,
  setIndicatorConfig,
  indicatorConfig = [],
  symbol
}) => {
  const [lines, setLines] = useState([]);

  const location = useLocation();

  const ScaleProvider =
    discontinuousTimeScaleProviderBuilder().inputDateAccessor(
      (d) => new Date(d.date)
    );
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  const [chartMainItemInitialPosition, setChartMainItemInitialPosition] =
    useState({
      top: "",
      left: "",
    });

  const aboveChartCounterRef = useRef(0); // Using a ref to track aboveChartCounter

  const [canvasStyle, setCanvasStyle] = useState({
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 7,
  });

  useEffect(() => {
    getChartMainItemPosition();

    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    const position = getChartMainItemPosition();

    setChartMainItemInitialPosition({
      top: position.top,
      left: position.left,
    });

    // Cleanup the event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getChartMainItemPosition = () => {
    let position = {
      top: "",
      left: "",
    };

    const chartDiv = document.querySelector(".chart-item.chart-main");

    if (chartDiv) {
      // Get the bounding rectangle of the element
      const rect = chartDiv.getBoundingClientRect();

      // Extract the position
      position = {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
      };
    } else {
      console.log("Element not found");
    }

    return position;
  };

  const height = isMobileView
    ? window.innerHeight * 0.5
    : window.innerHeight * 0.7;

  let width = window.innerWidth - 256 - 15 - 100;

  if(location.pathname !== `/buysell` && location.pathname !== `/charts`) {
    width = window.innerWidth - 80 - 326 - 100;
  }

  if (window.innerWidth < 768) {
    width = window.innerWidth - 15;
  }
  const margin = { left: 0, right: 48, top: 0, bottom: 24 };
  let { data, xScale, yScale, xAccessor, displayXAccessor } =
    ScaleProvider(initialData);
  const pricesDisplayFormat = format(".0f");
  const max = xAccessor(data[data.length - 1]);
  const min = xAccessor(data[0]);
  let xExtents = [min, max];
  const gridHeight = height - margin.top - margin.bottom;
  const mobileMax = xAccessor(data[data.length - 1]);
  const mobileMin = xAccessor(data[0]);
  const totalHeight = gridHeight;
  const chartHeight = gridHeight / 2;
  const [legendItems, setLegendItems] = useState([]);
  const [updatedLegendItems, setUpdatedLegendItems] = useState([]);
  let xmobileExtents = [mobileMin, mobileMax];
  const barChartHeight = gridHeight / 4;
  const isChartsRoute = location.pathname === "/charts";
  const barChartOrigin = (_, h) => [0, h - barChartHeight + 12];
  const barChartExtents = (data) => {
    return data.volume;
  };

  const candleChartExtents = (data) => {
    return [data.high, data.low];
  };

  const volumeColor = (data) => {
    return data.close > data.open
      ? "rgba(38, 166, 154, 0.3)"
      : "rgba(239, 83, 80, 0.3)";
  };

  const volumeSeries = (data) => {
    return data.volume;
  };

  const calculateADL = (data) => {
    let adl = 0; // Start with an ADL of 0
    return data.map((datum) => {
      const { close, high, low, volume } = datum;
      const moneyFlowMultiplier = (close - low - (high - close)) / (high - low);
      const moneyFlowVolume = moneyFlowMultiplier * volume;
      adl += moneyFlowVolume; // Accumulate the Money Flow Volume to get the ADL
      return {
        ...datum,
        adl, // Add the ADL to the datum
      };
    });
  };

  const dataWithADL = calculateADL(data);

  const Legend = ({ items }) => (
    <div className="absolute top-6 left-1.5 z-10">
      {items.map((item, index) => (
        <div
          key={index}
          className="flex items-center legend-items"
          //style={{ alignItems: "center" }}
        >
          <div
            className="md:w-3.5 md:h-3.5 w-2 h-2 mr-0.5 legend-item-color"
            style={{ backgroundColor: item.color }} // Keep dynamic color inline
          />
          <div className="md:text-sm text-xs legend-item-text">
            {item.label} ({item.value})
          </div>
        </div>
      ))}
    </div>
  );

  const newDate = new Date();

  const Legend1 = ({ items }) => (
    <div style={{ position: "absolute", top: 4, left: 5, zIndex: 5 }}>
      <>
        <div className={"flex flex-col"}>
          <p className={"font-samibold md:text-sm text-xs"}>
            {symbol} (
            <span className="font-samibold">
              {moment(newDate).local().format("MMMM DD, YYYY hh:mm A")}
            </span>
            )
          </p>
        </div>
      </>
    </div>
  );
  const gridWidth = width - margin.left - margin.right;
  const showGrid = gridSetting.grid;
  const yGrid = showGrid
    ? {
        showGridLines: true,
        innerTickSize: -1 * gridWidth,
        tickStrokeDasharray: gridSetting.style,
        // tickStrokeOpacity: gridSetting.opacity,
        tickStrokeWidth: gridSetting.strokeWidth,
        gridLinesStrokeStyle: "#D3D3D3",
      }
    : {};
  const xGrid = showGrid
    ? {
        showGridLines: true,
        innerTickSize: -1 * gridHeight,
        tickStrokeDasharray: gridSetting.style,
        // tickStrokeOpacity: gridSetting.opacity,
        tickStrokeWidth: gridSetting.strokeWidth,
        gridLinesStrokeStyle: "#D3D3D3",
      }
    : {};

  useEffect(() => {
    setLines([]);
    setSetting({
      ...setting,
      clearOverlay: false,
      enableBollinger: false,
    });
    setLegendItems([]);
  }, [setting.clearOverlay]);

  const isEffectivelyEmpty = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) {
      return true;
    }
    // Check if all elements in the array are empty arrays
    return arr.every((item) => Array.isArray(item) && item.length === 0);
  };

  useEffect(() => {
    setSetting({
      ...setting,
      clearIndicator: false,
    });
    setIndicatorConfig({
      indicatorConfig: [],
    });
    setSetting({
      ...setting,
      overlayParams: [],
    });
  }, [setting.clearIndicator, symbol]);

  const renderLineSeries = () => {
    return <>{lines}</>;
  };

  const getColorByLabelAndValue = (label, value, labelColorData) => {
    const matchedItem = labelColorData.find(
      (item) =>
        item.label.toLowerCase() === label.toLowerCase() && item.value === value
    );
    return matchedItem ? matchedItem.color : null;
  };

  useEffect(() => {
    let updatedLine = [];

    setting.overlayParams.forEach((item, index) => {
      if (
        item.inputValue === undefined ||
        item.inputValue === "" ||
        item.selectValue === ""
      ) {
        return;
      }
      if (item.selectValue === "sma") {
        updatedLine.push(
          <LineSeries
            key={`sma-top-${uuidv4()}`}
            yAccessor={(d) => d?.chart[index]?.sma?.sma}
            //strokeStyle="#2ca02c"
            strokeStyle={item.color}
            curve={curveMonotoneX}
          />
        );
      }

      if (item.selectValue === "ema") {
        updatedLine.push(
          <LineSeries
            key={`ema-top-${uuidv4()}`}
            yAccessor={(d) =>
              d.chart[index] && d.chart[index].ema && d.chart[index].ema.ema
            }
            //strokeStyle="#FF00FF"
            strokeStyle={item.color}
          />
        );
        // updatedLine.push(
        //   <LineSeries
        //     key={`ema-top-${uuidv4()}`}
        //     yAccessor={(d) =>
        //       d.chart[index] && d.chart[index].ema && d.chart[index].ema.close
        //     }
        //     strokeStyle="#00FF00"
        //   />
        // );
      }

      if (item.selectValue === "psar") {
        updatedLine.push(
          <>
            <SARSeries
              key={`psar-top-${uuidv4()}`}
              yAccessor={(d) =>
                d.chart[index] &&
                d.chart[index].psar &&
                d.chart[index].psar.psarl
              }
              //fillStyle={{ falling: "#000", raising: "#000" }}
              fillStyle={{ falling: item.color, raising: item.color }}
              highlightOnHover={true}
            />
            <SARSeries
              key={`psar-bottom-${uuidv4()}`}
              yAccessor={(d) =>
                d.chart[index] &&
                d.chart[index].psar &&
                d.chart[index].psar.psars
              }
              //fillStyle={{ falling: "#000", raising: "#000" }}
              fillStyle={{ falling: item.color, raising: item.color }}
              highlightOnHover={true}
            />
          </>
        );
      }

      if (item.selectValue === "bb") {
        updatedLine.push(
          <LineSeries
            key={`bb-top-${uuidv4()}`}
            yAccessor={(d) =>
              d.chart[index] && d.chart[index].bb && d.chart[index].bb.top
            }
            strokeStyle="#2ca02c"
          />
        );
        updatedLine.push(
          <LineSeries
            key={`bb-top-${uuidv4()}`}
            yAccessor={(d) =>
              d.chart[index] && d.chart[index].bb && d.chart[index].bb.bottom
            }
            strokeStyle="#FF00FF"
          />
        );
        updatedLine.push(
          <LineSeries
            key={`bb-top-${uuidv4()}`}
            yAccessor={(d) =>
              d.chart[index] && d.chart[index].bb && d.chart[index].bb.middle
            }
            //strokeStyle="#00FFF0"
            strokeStyle={item.color}
          />
        );
      }
    });
    setLines(updatedLine);
  }, [setting.overlayParams]);

  const [yAxisLabelX, yAxisLabelY] = [
    width - margin.left - 45,
    (height - margin.top - margin.bottom) / 2,
  ];

  const mainChart = (
    <div style={{ position: "relative" }} className={`chart-item chart-main`}>
      {isChartsRoute && (
        <>
          <Legend1 items={legendItems} />
        </>
      )}
      <Legend items={legendItems} />
      <ChartCanvas
        height={height}
        ratio={3}
        width={width}
        padding={{ left: 0, right: 15 }}
        data={dataWithADL}
        displayXAccessor={displayXAccessor}
        seriesName="Data"
        xScale={xScale}
        xAccessor={xAccessor}
        xExtents={setting.isMobile ? xmobileExtents : xExtents}
        disableZoom={true}
        disablePan={true}
        disableInteraction={true}
        zoomAnchor={lastVisibleItemBasedZoomAnchor}
      >
        {setting?.title && (
          <Label
            x={(width - margin.left - margin.right) / 2}
            y={30}
            fontSize="30"
            fillStyle="#153A5B"
            text={setting?.titleText}
          />
        )}

        <Chart
          id={2}
          height={barChartHeight}
          origin={barChartOrigin}
          yExtents={barChartExtents}
        >
          {setting.chartOverlay === "on" ? (
            <BarSeries fillStyle={volumeColor} yAccessor={volumeSeries} />
          ) : null}
        </Chart>
        <Chart id={3} height={totalHeight} yExtents={candleChartExtents}>
          <XAxis
            strokeStyle={"#8d8d8d"}
            tickStrokeStyle={"#8d8d8d"}
            showTicks={true}
            {...xGrid}
          />
          <YAxis
            strokeStyle={"#8d8d8d"}
            tickStrokeStyle={"#8d8d8d"}
            showTicks={true}
            tickFormat={(value) => nFormatter(value, 2)}
            {...yGrid}
          />
          <XAxis
            strokeStyle={"#8d8d8d"}
            tickStrokeStyle={"#8d8d8d"}
            showTicks={false}
            xZoomHeight={1}
            tickPadding={0}
            axisAt={"top"}
            orient={"top"}
          />
          <YAxis
            strokeStyle={"#8d8d8d"}
            tickStrokeStyle={"#8d8d8d"}
            showTicks={false}
            yZoomWidth={1}
            tickPadding={0}
            axisAt={"left"}
            orient={"left"}
          />

          {/* <CustomGrid xScale={xScale} yScale={yScale} chartHeight={height} chartWidth={width} /> */}
          {/*<LineSeries*/}
          {/*  key={12}*/}
          {/*  data={psarData}*/}
          {/*  yAccessor={(d) => d.PSARs}*/}
          {/*  strokeStyle="#00ff00"*/}
          {/*/>*/}
          {setting.enablePrice ? (
            <Annotate
              with={LabelAnnotation}
              when={(d, i) => {
                const condition = i % 4 === 0 && d.close > max - 5;
                const condition1 = i % 4 === 0;
                console.log("Condition check:", condition, d.close);
                return !condition ? condition1 : condition;
              }}
              usingProps={{
                text: (d) => `${d.close.toFixed(2)}`,
                fill: "#000000", // Annotation text color
                textAnchor: "middle",
                fontFamily: "Helvetica",
                fontSize: 11,
                tooltip: "Parabolic SAR",
                x: ({ xScale, xAccessor, datum }) => xScale(xAccessor(datum)), // Position the annotation based on the X value
                y: ({ yScale, datum }) => yScale(datum.close), // Position the annotation based on the PSAR value
              }}
            />
          ) : null}

          {setting.chartType === "candlestick" ? <CandlestickSeries /> : null}
          {setting.chartType === "solid" ? (
            <LineSeries
              yAccessor={(d) => d.close}
              strokeStyle="blue"
              strokeDasharray="none"
              strokeWidth={2}
            />
          ) : null}
          {renderLineSeries()}

          <MouseCoordinateY
            at="left"
            orient="left"
            rectWidth={margin.right}
            displayFormat={pricesDisplayFormat}
          />

          <MovingAverageTooltip origin={[8, 24]} options={[]} />

          {setting.quote ? <OHLCTooltip origin={[8, 16]} /> : null}
          {setting.chartType === "ohlc" ? <OHLCSeries /> : null}

          <MouseCoordinateY
            at="right"
            orient="right"
            displayFormat={format(".2f")}
          />
          <MouseCoordinateY
            at="left"
            orient="left"
            displayFormat={format(".2f")}
          />

          {setting?.xLabel && (
            <Label
              x={(width - margin.left - margin.right) / 2}
              y={height + 29}
              fontSize="25"
              fillStyle="#153A5B"
              text={setting?.xLabelText}
            />
          )}

          {setting?.yLabel && (
            <Label
              x={yAxisLabelX}
              y={yAxisLabelY}
              fillStyle="#153A5B"
              rotate={-90}
              fontSize="25"
              text={setting?.yLabelText}
            />
          )}
        </Chart>
        <CrossHairCursor />
      </ChartCanvas>
    </div>
  );

  const generateRandomColor = () => {
    return (
      "#" +
      Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, "0")
    );
  };

  const assignUniqueColors = (data) => {
    const labelSeen = {};

    return data.map((item) => {
      const label = item.label;
      if (label in labelSeen) {
        return { ...item, color: generateRandomColor() };
      } else {
        labelSeen[label] = item.color;
        return item;
      }
    });
  };

  /* useEffect(() => {
    const activeOverlays = setting.overlayParams
      .map((overlay) => {
        if (overlay.selectValue !== "" && overlay.inputValue !== "") {
          switch (overlay.selectValue) {
            case "bb":
              return {
                label: "BB",
                color: "#00FFF0",
                value: overlay.inputValue,
              };
            case "ema":
              return {
                label: "EMA",
                color: "#FF00FF",
                value: overlay.inputValue,
              };
            case "psar":
              return {
                label: "PSAR",
                color: "black",
                value: overlay.inputValue,
              };
            case "sma":
              return {
                label: "SMA",
                color: "#2ca02c",
                value: overlay.inputValue,
              };
            default:
              return null;
          }
        }
      })
      .filter(Boolean); // Filter out any null entries

    // SD
    const updatedData = assignUniqueColors(activeOverlays);

    setUpdatedLegendItems(updatedData);
    setLegendItems(updatedData);
  }, [setting.overlayParams]); */

  // SD
  useEffect(() => {
    const activeOverlays = setting.overlayParams
      .map((overlay) => {
        if (overlay.selectValue !== "" && overlay.inputValue !== "") {
          switch (overlay.selectValue) {
            case "bb":
              return {
                label: "BB",
                color: overlay.color,
                value: overlay.inputValue,
              };
            case "ema":
              return {
                label: "EMA",
                color: overlay.color,
                value: overlay.inputValue,
              };
            case "psar":
              return {
                label: "PSAR",
                color: overlay.color,
                value: overlay.inputValue,
              };
            case "sma":
              return {
                label: "SMA",
                color: overlay.color,
                value: overlay.inputValue,
              };
            default:
              return null;
          }
        }
      })
      .filter(Boolean); // Filter out any null entries

    setLegendItems(activeOverlays);
  }, [setting.overlayParams]);

  const renderIndicators = () => {
    const aboveChart = [];
    const belowChart = [];
    const indicatorCharts = [];

    if (isEffectivelyEmpty(indicatorConfig)) {
      return mainChart;
    }

    indicatorConfig.forEach((indicator, index) => {
      const chartItem = (
        <div
          key={`chart-canvas-container-${index}`}
          style={{ position: "relative" }}
          className={`chart-item`}
        >
          <div style={{ position: "absolute", top: 0, left: 18, zIndex: 5 }}>
            <div
              style={{ display: "flex", alignItems: "center", marginBottom: 5 }}
            >
              <div
                style={{
                  width: 14,
                  height: 14,
                  backgroundColor: indicator.strokeStyle,
                  marginRight: 5,
                }}
              />
              <div>{indicator.id}</div>
            </div>
          </div>
          <div style={{ position: "relative" }} className={`abcd`}>
            {indicator.id === "MACDH" ? (
              <MACDHIndicator
                height={chartHeight}
                ratio={3}
                width={width}
                chartData={indicator?.data ?? []}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={xExtents}
              />
            ) : indicator.id === "MACD" ? (
              <MACDIndicator
                height={chartHeight}
                ratio={3}
                width={width}
                chartData={indicator?.data ?? []}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={xExtents}
              />
            ) : indicator.id === "PPO" ? (
              <PPOIndicator
                height={chartHeight}
                ratio={3}
                width={width}
                chartData={indicator?.data ?? []}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={xExtents}
              />
            ) : indicator.id === "PVO" ? (
              <PVOIndicator
                height={chartHeight}
                ratio={3}
                width={width}
                chartData={indicator?.data ?? []}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={xExtents}
              />
            ) : indicator.id === "CCI" ? (
              <CCIIndicator
                height={chartHeight}
                ratio={3}
                width={width}
                initialData={indicator?.data ?? []}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={xExtents}
              />
            ) : indicator.id === "CMF" ? (
              <CMFIndicator
                height={chartHeight}
                ratio={3}
                width={width}
                initialData={indicator?.data ?? []}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={xExtents}
              />
            ) : indicator.id === "RSI" ? (
              <RSIIndicator
                height={chartHeight}
                ratio={3}
                width={width}
                initialData={indicator?.data ?? []}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={xExtents}
              />
            ) : indicator.id === "MFI" ? (
              <MFIIndicator
                height={chartHeight}
                ratio={3}
                width={width}
                initialData={indicator?.data ?? []}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={xExtents}
              />
            ) : indicator.id === "EFI" ? (
              <EFIndicator
                height={chartHeight}
                ratio={3}
                width={width}
                initialData={indicator?.data ?? []}
                displayXAccessor={displayXAccessor}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={xExtents}
              />
            ) : (
              <ChartCanvas
                height={chartHeight}
                ratio={3}
                width={width}
                data={data}
                displayXAccessor={displayXAccessor}
                seriesName={"Data" + index}
                xScale={xScale}
                xAccessor={xAccessor}
                xExtents={setting.isMobile ? xmobileExtents : xExtents}
                disableZoom={true}
                disablePan={true}
                disableInteraction={true}
                zoomAnchor={lastVisibleItemBasedZoomAnchor}
              >
                <Chart
                  key={"indicator" + index}
                  id={indicator.id}
                  yExtents={indicator.yExtents}
                  origin={(w, h) => [0, 0]}
                >
                  <XAxis />
                  <YAxis
                    tickFormat={(value) => nFormatter(value, 2)}
                    ticks={5}
                  />
                  <XAxis
                    showTicks={false}
                    xZoomHeight={1}
                    tickPadding={0}
                    axisAt={"top"}
                    orient={"top"}
                  />
                  <YAxis
                    showTicks={false}
                    yZoomWidth={1}
                    tickPadding={0}
                    axisAt={"left"}
                    orient={"left"}
                  />
                  {indicator.lineConfig.map((item, index) => {
                    if (data.macdh?.macdh !== undefined) {
                    }
                    return (
                      <>
                        {item.lineType ? (
                          <LineSeries
                            id={indicator.id + index}
                            yAccessor={item.yAccessor}
                            strokeStyle={item.strokeStyle}
                          />
                        ) : (
                          <BarSeries
                            yAccessor={item.yAccessor}
                            fill={(d) => (d.macdh.macdh > 0 ? "green" : "red")} // Green bars for positive values, red for negative
                          />
                        )}
                      </>
                    );
                  })}
                </Chart>
              </ChartCanvas>
            )}
          </div>
        </div>
      );

      if (indicator.position === "above" && indicator.selectValue !== "") {
        aboveChart.push(chartItem);

        aboveChartCounterRef.current += 1; // Update the ref value
      }
      if (indicator.position === "below") {
        belowChart.push(chartItem);
      }
    });
    indicatorCharts.push(aboveChart);
    indicatorCharts.push(mainChart);
    indicatorCharts.push(belowChart);

    if (aboveChart.length == 0) {
      aboveChartCounterRef.current = 0;
    }

    return indicatorCharts;
  };

  useEffect(() => {
    if (aboveChartCounterRef.current >= 1) {
      const position = getChartMainItemPosition();

      const newTop = position.top - chartMainItemInitialPosition.top;

      setCanvasStyle((prevStyle) => ({
        ...prevStyle,
        top: newTop,
      }));
    } else {
      setCanvasStyle((prevStyle) => ({
        ...prevStyle,
        top: 0,
      }));
    }
    const event = new CustomEvent("indicatorChangeEvent", {
      detail: true,
    });
    document.dispatchEvent(event);
  }, [indicatorConfig]); // Depend on indicatorConfig

  return (
    <>
      <div
        style={
          setting.colorMode === "light"
            ? {
                backgroundColor: "white",
                position: "relative",
                margin: "10px 0px",
                // border: "1px solid #D3D3D3", // light gray border
              }
            : { backgroundColor: "#00000096", position: "relative" }
        }
      >
        {renderIndicators()}

        <AnnotationStage
          style={canvasStyle}
          setting={setting}
          width={width}
          height={height}
          data={data}
        />
      </div>
    </>
  );
};

export default ChartComp;
